
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { DialogFooter } from "@/components/ui/dialog";
import { Organization } from "@/types/flight";
import { Profile } from "@/types/auth";
import { UserFormFields } from "./user-form/UserFormFields";
import { useUserFormSubmission } from "./user-form/useUserFormSubmission";
import { newUserSchema, updateUserSchema, NewUserFormValues, UpdateUserFormValues } from "./user-form/schemas";

interface UserFormProps {
  user?: Profile | null;
  organizations: Organization[];
  onSubmit: () => void;
}

export function UserForm({ user, organizations, onSubmit }: UserFormProps) {
  const isEditing = !!user;
  
  const form = useForm<NewUserFormValues | UpdateUserFormValues>({
    resolver: zodResolver(isEditing ? updateUserSchema : newUserSchema),
    defaultValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: "",
      ...(isEditing ? {} : { password: "" }),
      role: user?.role || "public",
      organization_id: user?.organization_id || null,
      is_active: user?.is_active ?? true,
    },
  });

  const { isSubmitting, handleSubmit } = useUserFormSubmission(user || null, onSubmit);

  const shouldShowOrgField = form.watch('role') === 'organization';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <UserFormFields
          control={form.control}
          isEditing={isEditing}
          shouldShowOrgField={shouldShowOrgField}
          organizations={organizations}
        />

        <DialogFooter>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : user ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
